import { useEffect, useRef } from 'react';

export const CODE_LENGTH = 6;

export const CodeInput = ({ value = '', onChange, onComplete, error, disabled, autoFocus }) => {
  const refs = useRef([]);
  const digits = Array.from({ length: CODE_LENGTH }, (_, i) => value[i] || '');

  useEffect(() => {
    if (!disabled && !value) refs.current[0]?.focus();
  }, [disabled, value]);

  const focus = index => refs.current[Math.max(0, Math.min(index, CODE_LENGTH - 1))]?.focus();

  const update = (next, focusIndex) => {
    const clean = next.replace(/\D/g, '').slice(0, CODE_LENGTH);
    onChange(clean);
    focus(focusIndex);
    if (clean.length === CODE_LENGTH && clean !== value) onComplete?.(clean);
  };

  const handleChange = (index, e) => {
    const typed = e.target.value.replace(/\D/g, '');
    if (!typed) return;
    update(value.slice(0, index) + typed + value.slice(index + typed.length), index + typed.length);
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (digits[index]) {
        update(value.slice(0, index) + value.slice(index + 1), index);
      } else if (index > 0) {
        update(value.slice(0, index - 1) + value.slice(index), index - 1);
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      focus(index - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      focus(Math.min(index + 1, value.length));
    }
  };

  // Digits are kept contiguous, so focus never lands past the first empty cell.
  const handleFocus = (index, e) => {
    if (index > value.length) {
      focus(value.length);
      return;
    }
    e.target.select();
  };

  return (
    <div className={`wm-code${error ? ' is-error' : ''}`}>
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={el => (refs.current[index] = el)}
          className={`wm-code__cell${digit ? ' is-filled' : ''}`}
          value={digit}
          inputMode='numeric'
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          aria-label={`${index + 1}`}
          disabled={disabled}
          autoFocus={autoFocus && index === 0}
          onChange={e => handleChange(index, e)}
          onKeyDown={e => handleKeyDown(index, e)}
          onFocus={e => handleFocus(index, e)}
        />
      ))}
    </div>
  );
};
